/**
 * Browser Push Notification Service
 * Handles Web Notifications API with Service Worker integration
 */

/**
 * Request user permission for notifications
 */
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return false;
    }

    // Already granted
    if (Notification.permission === 'granted') {
        return true;
    }

    // Already denied
    if (Notification.permission === 'denied') {
        return false;
    }

    // Request permission
    try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
    }
};

/**
 * Check if notifications are enabled
 */
export const isNotificationEnabled = () => {
    return 'Notification' in window && Notification.permission === 'granted';
};

/**
 * Send a browser notification via Service Worker
 */
export const sendNotification = (title, options = {}) => {
    if (!isNotificationEnabled()) {
        return;
    }

    try {
        if (navigator.serviceWorker?.controller) {
            // Send via Service Worker
            navigator.serviceWorker.controller.postMessage({
                type: 'SHOW_NOTIFICATION',
                title,
                options: {
                    icon: '/favicon.ico',
                    badge: '/favicon.ico',
                    tag: 'app-notification',
                    requireInteraction: false,
                    ...options,
                }
            });
        } else {
            // Fallback: show directly
            new Notification(title, {
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                ...options,
            });
        }
    } catch (error) {
        console.error('Error sending notification:', error);
    }
};

/**
 * Success notification
 */
export const notifySuccess = (message, title = 'Success') => {
    sendNotification(title, {
        body: message,
        tag: 'success-notification',
        requireInteraction: false,
    });
};

/**
 * Error notification
 */
export const notifyError = (message, title = 'Error') => {
    sendNotification(title, {
        body: message,
        tag: 'error-notification',
        requireInteraction: true, // Keep on screen longer for errors
    });
};

/**
 * Info notification
 */
export const notifyInfo = (message, title = 'Information') => {
    sendNotification(title, {
        body: message,
        tag: 'info-notification',
        requireInteraction: false,
    });
};

/**
 * Warning notification
 */
export const notifyWarning = (message, title = 'Warning') => {
    sendNotification(title, {
        body: message,
        tag: 'warning-notification',
        requireInteraction: false,
    });
};

/**
 * Custom notification with full control
 */
export const notifyCustom = (title, options = {}) => {
    sendNotification(title, options);
};